import { UniqueIdentifier } from '@dnd-kit/core';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import { useTrackPaywallClick } from '@shared/data/billing/hooks/useTrackPaywall';
import { Entity } from '@shared/data/entity/Entity';
import { useUpgradeModal } from '@shared/scenes/billing';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import { OpenEntityHandler } from '@shared/scenes/cards/collectionView/hooks';
import { MBCardEmptyState } from '@shared/scenes/cards/components/emptyState';
import { PRICING_LIMIT_HISTORY_DAYS } from '@shared/styles';
import { API } from '@shared/types/api';
import { getTestId } from '@shared/util/getTestId';
import { i18n } from '@shared/util/i18n';
import { CardListFooter } from './CardListFooter';
import { CardListItem } from './CardListItem';
import { useDraggableContext } from './DraggableContext';
import { VirtualCardList } from './VirtualCardList';
import ListViewCardStyles from './ListViewCardStyles.module.css';
import styles from './VirtualCardList.module.css';

interface Props {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  canUpdateCard: boolean;
  entities: Entity[];
  openEntity: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  showMore(): void;
  hasMore: boolean;
  isMoreLoading: boolean;
  isCardsLoading: boolean;
  isGroup: boolean;
  hasOverflowMenu?: boolean;
  isList?: boolean;
  isBoard?: boolean;
  isNarrowBoard?: boolean;
  groupId?: UniqueIdentifier;
  testID?: string;
}

export function CardList({
  workflowTemplate,
  viewTemplate,
  canUpdateCard,
  entities,
  openEntity,
  getIsCardBeforePaidCutoff,
  hasMore,
  showMore,
  isCardsLoading,
  isMoreLoading,
  isGroup,
  hasOverflowMenu = true,
  isList,
  isBoard,
  isNarrowBoard,
  groupId,
  testID,
}: Props) {
  const { show: showUpgradeModal } = useUpgradeModal();
  const reportRedactedClick = useTrackPaywallClick();
  const analytics = useMemo(
    () => new CollectionViewAnalytics(workflowTemplate.id, viewTemplate.id),
    [workflowTemplate.id, viewTemplate.id],
  );

  const showModal = useCallback(() => {
    reportRedactedClick();
    showUpgradeModal({
      title: i18n('card.upgradeModal.title', { count: PRICING_LIMIT_HISTORY_DAYS }),
      description: i18n('card.upgradeModal.description'),
    });
  }, [showUpgradeModal, reportRedactedClick]);

  const renderItem = useCallback(
    ({ item, index }: { item: Entity; index: number }) => {
      const isRedacted = getIsCardBeforePaidCutoff(item);

      return (
        <CardListItem
          viewTemplate={viewTemplate}
          workflowTemplate={workflowTemplate}
          openEntity={isRedacted ? showModal : openEntity}
          getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
          entity={item}
          canUpdateCard={canUpdateCard}
          cardStyles={isList ? ListViewCardStyles : undefined}
          isBoard={isBoard}
          isRedacted={isRedacted}
          analytics={analytics}
          hasOverflowMenu={hasOverflowMenu}
          testID={getTestId('card-list-item', groupId, index)}
          groupId={groupId}
        />
      );
    },
    [
      analytics,
      canUpdateCard,
      getIsCardBeforePaidCutoff,
      groupId,
      hasOverflowMenu,
      isBoard,
      isList,
      openEntity,
      showModal,
      viewTemplate,
      workflowTemplate,
    ],
  );

  const { isDragging } = useDraggableContext();
  const isLoading = isCardsLoading || isMoreLoading;
  const isEmptyState = isEmpty(entities) && !isLoading;
  const emptyTitle = isGroup ? i18n('card.table.emptyGroup.title') : i18n('emptyCardList.title');
  const emptyMessage = isGroup
    ? i18n('card.table.emptyGroup.description')
    : i18n('emptyCardList.description');

  const contentClassName = clsx(
    isList && styles.list,
    isBoard && styles.board,
    isNarrowBoard && styles.narrowUngroupedBoard,
  );

  return (
    <VirtualCardList
      entities={entities}
      renderItem={renderItem}
      contentClassName={contentClassName}
      testID={testID}
      ListFooterComponent={
        <>
          {isEmptyState && !isDragging && (
            <MBCardEmptyState title={emptyTitle} message={emptyMessage} />
          )}
          <CardListFooter showMore={showMore} hasMore={hasMore} isMoreLoading={isMoreLoading} />
        </>
      }
    />
  );
}
